import React, { useState, useEffect } from 'react';
import {
  Box,
  TextField,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Typography,
  Switch,
  FormControlLabel,
  Grid,
  Paper,
  Divider,
  CircularProgress
} from '@mui/material';
import { Chapitre, Matiere } from '../../types';
import CurriculumApiService from '../../services/curriculumApi';
import { useEnvironment } from '../../contexts/EnvironmentContext';
import type { FieldChangeEvent } from '../../types/forms';
import { logger } from '../../lib/logger';

interface ChapitreFormProps {
  chapitre?: Chapitre | null;
  defaultMatiereId?: string;
  onSubmit: (data: Omit<Chapitre, 'id'>) => void;
  onCancel: () => void;
  /** Matières proposées : fournies, le formulaire ne charge plus toute la collection. */
  matieres?: Pick<Matiere, 'id' | 'nom'>[];
}

const ChapitreForm: React.FC<ChapitreFormProps> = ({ chapitre, defaultMatiereId, onSubmit, onCancel, matieres: matieresFournies }) => {
  const { environment } = useEnvironment();


  const [formData, setFormData] = useState<Omit<Chapitre, 'id'>>({
    titre: '',
    description: '',
    ordre: 1,
    matiereId: defaultMatiereId || '',
    niveauDifficulte: undefined,
    tempsEstimeTotalMinutes: undefined,
    estActif: true,
    dateCreation: new Date().toISOString().split('T')[0],
    dateModification: new Date().toISOString().split('T')[0],
    dateChargement: undefined,
    environnement: undefined,
    imageUrl: undefined,
    thumbnailLatex: undefined,
    statut: 'draft' // Par défaut en brouillon
  });

  const [matieresChargees, setMatieres] = useState<Pick<Matiere, 'id' | 'nom'>[]>([]);
  const matieres = matieresFournies ?? matieresChargees;
  const listeFournie = !!matieresFournies;
  const [loadingMatieres, setLoadingMatieres] = useState(false);

  const niveauxDifficulte = [
    { value: 'facile', label: 'Facile' },
    { value: 'moyen', label: 'Moyen' },
    { value: 'difficile', label: 'Difficile' }
  ];

  // Charger les matières
  useEffect(() => {
    if (listeFournie) return;
    const loadMatieres = async () => {
      try {
        setLoadingMatieres(true);
        const matieresData = await CurriculumApiService.getMatieres(environment);
        setMatieres(matieresData);
      } catch (error) {
        logger.error('Erreur lors du chargement des matières:', error);
      } finally {
        setLoadingMatieres(false);
      }
    };

    loadMatieres();
  }, [environment, listeFournie]);

  useEffect(() => {
    if (chapitre) {
      // Données de base du chapitre
      const baseData: Omit<Chapitre, 'id'> = {
        titre: chapitre.titre || '',
        description: chapitre.description || '',
        ordre: chapitre.ordre || 1,
        matiereId: chapitre.matiereId || '',
        niveauDifficulte: chapitre.niveauDifficulte,
        tempsEstimeTotalMinutes: chapitre.tempsEstimeTotalMinutes,
        estActif: chapitre.estActif ?? true,
        dateCreation: chapitre.dateCreation || new Date().toISOString().split('T')[0],
        dateModification: new Date().toISOString().split('T')[0],
        dateChargement: chapitre.dateChargement,
        environnement: chapitre.environnement,
        imageUrl: chapitre.imageUrl,
        thumbnailLatex: chapitre.thumbnailLatex,
        statut: chapitre.statut || 'draft',
        datePublication: chapitre.datePublication,
        historiqueStatut: chapitre.historiqueStatut
      };


      setFormData(baseData);
    }
  }, [chapitre]);

  const handleChange = (field: keyof Omit<Chapitre, 'id'>) => (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const value = event.target.type === 'checkbox'
      ? (event.target as HTMLInputElement).checked
      : event.target.value;

    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleSelectChange = (field: keyof Omit<Chapitre, 'id'>) => (
    event: FieldChangeEvent
  ) => {
    setFormData(prev => ({
      ...prev,
      [field]: event.target.value
    }));
  };

  const handleSubmit = (event: React.FormEvent) => {
    event.preventDefault();
    onSubmit(formData);
  };

  return (
    <Box component="form" onSubmit={handleSubmit} sx={{ mt: 2 }}>
      <Grid container spacing={3}>
        {/* Informations de base */}
        <Grid item xs={12}>
          <Paper sx={{ p: 2 }}>
            <Typography variant="h6" gutterBottom>Informations de base</Typography>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Titre du chapitre"
                  value={formData.titre}
                  onChange={handleChange('titre')}
                  required
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Description"
                  value={formData.description}
                  onChange={handleChange('description')}
                  multiline
                  rows={4}
                  required
                />
              </Grid>
              <Grid item xs={6} md={3}>
                <TextField
                  fullWidth
                  label="Ordre"
                  type="number"
                  value={formData.ordre}
                  onChange={handleChange('ordre')}
                  required
                  inputProps={{ min: 1 }}
                />
              </Grid>
              <Grid item xs={6} md={3}>
                <TextField
                  fullWidth
                  label="Temps estimé (minutes)"
                  type="number"
                  value={formData.tempsEstimeTotalMinutes || ''}
                  onChange={handleChange('tempsEstimeTotalMinutes')}
                  inputProps={{ min: 0 }}
                />
              </Grid>
              <Grid item xs={6} md={3}>
                <FormControl fullWidth required>
                  <InputLabel>Matière</InputLabel>
                  <Select
                    value={formData.matiereId}
                    onChange={handleSelectChange('matiereId')}
                    label="Matière"
                    disabled={loadingMatieres}
                  >
                    {loadingMatieres ? (
                      <MenuItem disabled>
                        <CircularProgress size={20} />
                        <span style={{ marginLeft: 8 }}>Chargement...</span>
                      </MenuItem>
                    ) : (
                      matieres.map((matiere) => (
                        <MenuItem key={matiere.id} value={matiere.id}>
                          {matiere.nom}
                        </MenuItem>
                      ))
                    )}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={6} md={3}>
                <FormControl fullWidth>
                  <InputLabel>Niveau de difficulté</InputLabel>
                  <Select
                    value={formData.niveauDifficulte || ''}
                    onChange={handleSelectChange('niveauDifficulte')}
                    label="Niveau de difficulté"
                  >
                    {niveauxDifficulte.map((niveau) => (
                      <MenuItem key={niveau.value} value={niveau.value}>
                        {niveau.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
            </Grid>
          </Paper>
        </Grid>

        {/* Dates */}
        <Grid item xs={12}>
          <Paper sx={{ p: 2 }}>
            <Typography variant="h6" gutterBottom>Dates</Typography>
            <Grid container spacing={2}>
              <Grid item xs={6}>
                <TextField
                  fullWidth
                  label="Date de création"
                  type="date"
                  value={formData.dateCreation}
                  onChange={handleChange('dateCreation')}
                  InputLabelProps={{
                    shrink: true,
                  }}
                  required
                />
              </Grid>
              <Grid item xs={6}>
                <TextField
                  fullWidth
                  label="Date de modification"
                  type="date"
                  value={formData.dateModification}
                  InputLabelProps={{
                    shrink: true,
                  }}
                  disabled
                />
              </Grid>
            </Grid>
          </Paper>
        </Grid>

        {/* Statut */}
        <Grid item xs={12}>
          <Paper sx={{ p: 2 }}>
            <Typography variant="h6" gutterBottom>Statut et activation</Typography>
            <Grid container spacing={2} alignItems="center">
              <Grid item xs={12} md={6}>
                <FormControl fullWidth>
                  <InputLabel>Statut</InputLabel>
                  <Select
                    value={formData.statut || 'draft'}
                    onChange={handleSelectChange('statut')}
                    required
                  >
                    <MenuItem value="draft">🔸 Brouillon</MenuItem>
                    <MenuItem value="published">✅ Publié</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} md={6}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={formData.estActif}
                      onChange={(e) => setFormData(prev => ({ ...prev, estActif: e.target.checked }))}
                    />
                  }
                  label="Chapitre actif"
                />
                <Typography variant="body2" color="textSecondary">
                  Un chapitre inactif ne sera pas visible dans l'application.
                </Typography>
              </Grid>
            </Grid>
          </Paper>
        </Grid>
      </Grid>

      <Divider sx={{ my: 3 }} />

      <Box display="flex" justifyContent="flex-end" gap={2}>
        <Button onClick={onCancel}>
          Annuler
        </Button>
        <Button type="submit" variant="contained">
          {chapitre ? 'Mettre à jour' : 'Créer'}
        </Button>
      </Box>
    </Box>
  );
};

export default ChapitreForm;